import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, CircularProgress, IconButton, InputAdornment, Link, MenuItem,
  Select, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import {
  patreonPostAPI, PatreonCatalogFacet, PatreonCatalogPost, PatreonEditSeed,
} from '../../services/api';

const postToSeed = (p: PatreonCatalogPost): PatreonEditSeed => ({
  patreon_post_id: p.post_id,
  post_type: p.post_type || undefined,
  title: p.title || '',
  body: p.content || '',
  tags: p.tags || [],
  collection: p.collections?.[0]?.id,
  podcast: p.podcasts?.[0]?.id,
  is_public: p.is_paid === false,
});

// A scrollable reference browser of past Patreon posts — pick a Collection or a
// Podcast feed and scroll every post's full title/description/tags, copying text
// into the draft you're writing without navigating away.

const htmlToText = (html: string | null): string => {
  if (!html) return '';
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || d.innerText || '').trim();
};

const copy = (s: string) => { try { navigator.clipboard.writeText(s); } catch { /* noop */ } };

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

const PostCard: React.FC<{ post: PatreonCatalogPost; onEdit: (p: PatreonCatalogPost) => void }> = ({ post, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const text = useMemo(() => htmlToText(post.content), [post.content]);
  const teaser = post.teaser_text || text;
  const shown = expanded || teaser.length <= 280 ? teaser : teaser.slice(0, 280) + '…';
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, mb: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {post.title || '(untitled)'}
        </Typography>
        <Stack direction="row">
          <Tooltip title="Edit in app">
            <IconButton size="small" onClick={() => onEdit(post)}>
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy title">
            <IconButton size="small" onClick={() => copy(post.title || '')}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {fmtDate(post.published_at)}
        {post.post_type ? ` · ${post.post_type}` : ''}
        {post.is_paid === false ? ' · public' : ''}
        {post.url ? <> · <Link href={post.url} target="_blank" rel="noreferrer">open ↗</Link></> : null}
      </Typography>
      {teaser && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', display: 'block' }}>
            {shown}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
            {teaser.length > 280 && (
              <Link component="button" variant="caption" onClick={() => setExpanded((x) => !x)}>
                {expanded ? 'less' : 'more'}
              </Link>
            )}
            <Link component="button" variant="caption" onClick={() => copy(text)}>copy description</Link>
          </Stack>
        </Box>
      )}
      {post.tags?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.5 }}>
          {post.tags.map((t) => <Chip key={t} size="small" label={t} sx={{ height: 18, fontSize: 10 }} />)}
        </Box>
      )}
    </Box>
  );
};

const PatreonCatalogReference: React.FC<{ onEdit?: (seed: PatreonEditSeed) => void }> = ({ onEdit }) => {
  const [tab, setTab] = useState<'collection' | 'podcast'>('collection');
  const [collections, setCollections] = useState<PatreonCatalogFacet[]>([]);
  const [podcasts, setPodcasts] = useState<PatreonCatalogFacet[]>([]);
  const [facetId, setFacetId] = useState('');
  const [q, setQ] = useState('');
  const [posts, setPosts] = useState<PatreonCatalogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          patreonPostAPI.catalogCollections().catch(() => ({ collections: [] })),
          patreonPostAPI.catalogPodcasts().catch(() => ({ podcasts: [] })),
        ]);
        setCollections(c.collections || []);
        setPodcasts(p.podcasts || []);
      } catch (e: any) { setErr(e?.message || String(e)); }
    })();
  }, []);

  // reset selected facet when switching tabs
  useEffect(() => { setFacetId(''); setPosts([]); }, [tab]);

  // load posts for the selected facet (+ search)
  useEffect(() => {
    // No facet/search → nothing to load. MUST clear loading here too: switching
    // tabs resets facetId, which cancels an in-flight fetch and re-enters this
    // branch; without clearing, the spinner would hang forever.
    if (!facetId && !q.trim()) { setPosts([]); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params: any = { include_content: true, limit: 100 };
        if (tab === 'collection') params.collection_id = facetId || undefined;
        else params.podcast_id = facetId || undefined;
        if (q.trim()) params.q = q.trim();
        const r = await patreonPostAPI.catalogPosts(params);
        if (alive) setPosts(r.posts || []);
      } catch (e: any) { if (alive) setErr(e?.message || String(e)); }
      finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [facetId, q, tab]);

  const facets = tab === 'collection' ? collections : podcasts;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography variant="overline" color="text.secondary">Reference — past posts</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ minHeight: 32, mb: 1 }}>
        <Tab value="collection" label={`Collections (${collections.length})`} sx={{ minHeight: 32, py: 0 }} />
        <Tab value="podcast" label={`Podcasts (${podcasts.length})`} sx={{ minHeight: 32, py: 0 }} />
      </Tabs>
      <Select size="small" value={facetId} displayEmpty fullWidth sx={{ mb: 1 }}
        onChange={(e) => setFacetId(e.target.value)}>
        <MenuItem value=""><em>{tab === 'collection' ? 'Pick a collection…' : 'Pick a podcast feed…'}</em></MenuItem>
        {facets.map((f) => (
          <MenuItem key={f.id} value={f.id}>{f.title} ({f.posts})</MenuItem>
        ))}
      </Select>
      <TextField size="small" fullWidth placeholder="Search titles…" value={q} sx={{ mb: 1 }}
        onChange={(e) => setQ(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      {err && <Typography variant="caption" color="error">{err}</Typography>}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
        {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>}
        {!loading && posts.length === 0 && (facetId || q) && (
          <Typography variant="caption" color="text.secondary">No posts.</Typography>
        )}
        {!loading && posts.map((p) => (
          <PostCard key={p.post_id} post={p} onEdit={(pp) => onEdit?.(postToSeed(pp))} />
        ))}
      </Box>
    </Box>
  );
};

export default PatreonCatalogReference;
